import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  InvoiceData,
  InvoiceItemData,
  InvoiceTaxConfig,
  TaxRate,
  TypeTaxAssignment
} from "../../../../../types/invoices";
import { BookingV4 } from "../../../../../types/bookings";
import { VenueConfig } from "../../../../../types/venues";
import {
  applyPreorderDeduction,
  buildLegacyDepositItems,
  buildLegacyPreorderItems,
  buildLegacyRefundItems,
  buildPreorderInvoiceItems,
  buildTieredTaxInvoiceItem,
  calculatePreorderTotal,
  createBlankInvoiceItem,
  getDepositsTypeId,
  getInvoiceLabelForType,
  groupDepositsByTaxRateIdsAndLabel,
  groupRefundsByTaxRateIdsAndLabel,
  isDeepEqual,
  mergePreorderItemsIntoExisting,
  removeOrphanedStructuralItems,
  resolveFallbackTaxConfig,
  shouldProcessRefundsAsItems
} from "./invoiceUtils";

interface UseInvoiceStateParams {
  booking: BookingV4 | undefined;
  latestInvoice: InvoiceData | undefined;
  taxRates: TaxRate[];
  venueConfig: VenueConfig | undefined;
  venueGroupConfig:
    | {
        override_booking_invoicing?: boolean;
        booking_invoicing?: Record<string, string>;
      }
    | undefined;
  typesCache:
    | Array<{ id: string; name: string; invoice_label?: string }>
    | undefined;
  invoiceTaxConfig: InvoiceTaxConfig | undefined;
  typeTaxAssignments: TypeTaxAssignment[] | undefined;
  invoiceTaxLocking: boolean;
  individualItemsEnabled: boolean;
  onClearSaveError?: () => void;
  venuePackages:
    | Array<{
        id: string;
        type_id?: string;
        name?: string;
        price?: number;
        base_price?: number;
      }>
    | undefined;
  venueMigrated: boolean;
}

interface InvoiceStateResult {
  invoice: InvoiceData | null;
  setInvoice: React.Dispatch<React.SetStateAction<InvoiceData | null>>;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  invoiceIsDirty: boolean;
  showDiscardConfirm: boolean;
  setShowDiscardConfirm: (show: boolean) => void;
  handleUpdateInvoice: (updates: Partial<InvoiceData>) => void;
  handleUpdateItem: (index: number, updates: Partial<InvoiceItemData>) => void;
  handleRemoveItem: (index: number) => void;
  handleAddBlankItem: () => void;
  handleAddRefundItems: () => void;
  handleAddDepositItems: () => void;
  handleAddPreorderItems: () => void;
  handleAddInvoice: () => void;
  handleEditInvoice: () => void;
  handleCancelEdit: () => void;
  confirmCancelEdit: () => void;
}

const useInvoiceState = ({
  booking,
  latestInvoice,
  taxRates,
  venueConfig,
  venueGroupConfig,
  typesCache,
  invoiceTaxConfig,
  typeTaxAssignments,
  invoiceTaxLocking,
  individualItemsEnabled,
  onClearSaveError,
  venuePackages,
  venueMigrated
}: UseInvoiceStateParams): InvoiceStateResult => {
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Set invoice from API data
  useEffect(() => {
    if (latestInvoice && !isEditing) {
      setInvoice(latestInvoice);
    }
  }, [latestInvoice, isEditing]);

  // Dirty tracking: detect unsaved changes (matches collins-ui invoiceIsDirty)
  const invoiceIsDirty = useMemo((): boolean => {
    if (!isEditing || !invoice) return false;
    if (!latestInvoice) return true; // New invoice is always dirty
    return !isDeepEqual(invoice, latestInvoice);
  }, [isEditing, invoice, latestInvoice]);

  const handleUpdateInvoice = useCallback(
    (updates: Partial<InvoiceData>): void => {
      setInvoice((prev) => (prev ? { ...prev, ...updates } : prev));
    },
    []
  );

  const handleUpdateItem = useCallback(
    (index: number, updates: Partial<InvoiceItemData>): void => {
      setInvoice((prev) => {
        if (!prev) return prev;
        const items = [...(prev.items ?? [])];
        items[index] = { ...items[index], ...updates };
        return { ...prev, items };
      });
    },
    []
  );

  const handleRemoveItem = useCallback((index: number): void => {
    setInvoice((prev) => {
      if (!prev) return prev;
      const items = [...(prev.items ?? [])];
      items.splice(index, 1);
      // Remove orphaned structural items (headings/footers) when the last
      // regular item of a type group is removed
      const cleanedItems = removeOrphanedStructuralItems(items);
      return { ...prev, items: cleanedItems };
    });
  }, []);

  const handleAddBlankItem = useCallback((): void => {
    const defaultRate = taxRates.length > 0 ? taxRates[0] : undefined;
    setInvoice((prev) => {
      if (!prev) return prev;
      const items = [
        ...(prev.items ?? []),
        createBlankInvoiceItem(defaultRate)
      ];
      return { ...prev, items };
    });
  }, [taxRates]);

  const handleAddRefundItems = useCallback((): void => {
    if (!booking) return;

    // Legacy path for non-migrated venues
    if (!venueMigrated) {
      const legacyItems = buildLegacyRefundItems(
        booking,
        taxRates,
        venueGroupConfig as {
          override_booking_invoicing?: boolean;
          booking_invoicing?: Record<string, string>;
        }
      );
      if (legacyItems.length === 0) return;
      setInvoice((prev) => {
        if (!prev) return prev;
        return { ...prev, items: [...(prev.items ?? []), ...legacyItems] };
      });
      return;
    }

    const refunds = booking.refunds ?? [];
    const deposits = booking.deposits ?? [];
    if (refunds.length === 0) return;

    const extendedDeposits = deposits.map((deposit) => ({
      amount:
        typeof deposit.amount === "number"
          ? deposit.amount
          : ((deposit.amount as { value?: number })?.value ?? 0),
      tax_rate_ids: deposit.tax_rate_ids ?? [],
      invoice_label: deposit.invoice_label ?? "",
      source: deposit.source ?? deposit.paid_type ?? "",
      id: deposit.id,
      refund_ids: deposit.refund_ids,
      refunds: deposit.refunds,
      type: deposit.type as string
    }));

    const depositsTypeId = getDepositsTypeId(typesCache);
    const depositLabel = getInvoiceLabelForType(
      "deposit_label",
      "Deposit",
      venueConfig ?? undefined,
      depositsTypeId,
      typesCache
    );
    const fallbackConfig = resolveFallbackTaxConfig({
      depositsTypeId,
      invoiceTaxConfig,
      typeTaxAssignments,
      taxRates,
      taxLocking: invoiceTaxLocking
    });

    const grouped = groupRefundsByTaxRateIdsAndLabel(refunds, extendedDeposits);
    const newItems: InvoiceItemData[] = [];

    for (const [, group] of grouped) {
      const matchingRates = group.tax_rate_ids
        .map((id) => taxRates.find((r) => (r._id ?? r.id) === id))
        .filter((r): r is TaxRate => r !== undefined);

      const defaultLabel = group.label ? group.label : "Deposit";
      const description =
        depositLabel !== "Deposit" ? depositLabel : defaultLabel;
      const negativeAmount = -Math.abs(group.totalAmount);

      newItems.push(
        buildTieredTaxInvoiceItem({
          description,
          amount: negativeAmount,
          matchingRates,
          fallbackConfig,
          taxRates,
          taxLocking: invoiceTaxLocking
        })
      );
    }

    setInvoice((prev) => {
      if (!prev) return prev;
      return { ...prev, items: [...(prev.items ?? []), ...newItems] };
    });
  }, [
    booking,
    taxRates,
    venueConfig,
    typesCache,
    invoiceTaxConfig,
    typeTaxAssignments,
    invoiceTaxLocking,
    venueMigrated
  ]);

  const handleAddDepositItems = useCallback((): void => {
    if (!booking) return;

    // Legacy path for non-migrated venues
    if (!venueMigrated) {
      const legacyItems = buildLegacyDepositItems(
        booking,
        taxRates,
        venueGroupConfig as {
          override_booking_invoicing?: boolean;
          booking_invoicing?: Record<string, string>;
        }
      );
      if (legacyItems.length === 0) return;
      setInvoice((prev) => {
        if (!prev) return prev;
        return { ...prev, items: [...(prev.items ?? []), ...legacyItems] };
      });
      return;
    }

    const deposits = booking.deposits ?? [];
    if (deposits.length === 0) return;

    const depositEntries = deposits.map((deposit) => {
      const rawAmount = deposit.amount;
      const amount =
        typeof rawAmount === "number"
          ? rawAmount
          : ((rawAmount as { value?: number })?.value ?? 0);
      return {
        amount,
        tax_rate_ids: deposit.tax_rate_ids ?? [],
        invoice_label: deposit.invoice_label ?? "",
        source: deposit.source ?? deposit.paid_type ?? ""
      };
    });

    // Deduct preorder totals from deposit amounts before grouping
    // to prevent double-counting (matches collins-ui applyDepositSourceOrdering)
    const preorders = booking.preorders ?? [];
    const preorderTotal = calculatePreorderTotal(preorders);
    const adjustedEntries =
      preorderTotal > 0
        ? applyPreorderDeduction(depositEntries, preorderTotal)
        : depositEntries;

    // Resolve the deposits type for tier-2 fallback
    const depositsTypeId = getDepositsTypeId(typesCache);
    const depositLabel = getInvoiceLabelForType(
      "deposit_label",
      "",
      venueConfig ?? undefined,
      depositsTypeId,
      typesCache
    );
    const fallbackConfig = resolveFallbackTaxConfig({
      depositsTypeId,
      invoiceTaxConfig,
      typeTaxAssignments,
      taxRates,
      taxLocking: invoiceTaxLocking
    });

    const grouped = groupDepositsByTaxRateIdsAndLabel(adjustedEntries);
    const newItems: InvoiceItemData[] = [];

    for (const [, group] of grouped) {
      const matchingRates = group.tax_rate_ids
        .map((id) => taxRates.find((r) => (r._id ?? r.id) === id))
        .filter((r): r is TaxRate => r !== undefined);

      const fallbackLabel = depositLabel !== "" ? depositLabel : "Deposit";
      const description = group.label ? group.label : fallbackLabel;

      newItems.push(
        buildTieredTaxInvoiceItem({
          description,
          amount: group.totalAmount,
          matchingRates,
          fallbackConfig,
          taxRates,
          taxLocking: invoiceTaxLocking
        })
      );
    }

    setInvoice((prev) => {
      if (!prev) return prev;
      return { ...prev, items: [...(prev.items ?? []), ...newItems] };
    });

    // Automatically add refund items in grouped mode (matches collins-ui behaviour)
    if (booking && shouldProcessRefundsAsItems(booking)) {
      handleAddRefundItems();
    }
  }, [
    booking,
    taxRates,
    venueConfig,
    typesCache,
    invoiceTaxConfig,
    typeTaxAssignments,
    invoiceTaxLocking,
    venueMigrated,
    handleAddRefundItems
  ]);

  const handleAddPreorderItems = useCallback((): void => {
    if (!booking) return;

    // Legacy path for non-migrated venues
    if (!venueMigrated) {
      const legacyItems = buildLegacyPreorderItems(
        booking,
        taxRates,
        venueGroupConfig as {
          override_booking_invoicing?: boolean;
          booking_invoicing?: Record<string, string>;
        }
      );
      if (legacyItems.length === 0) return;
      setInvoice((prev) => {
        if (!prev) return prev;
        return { ...prev, items: [...(prev.items ?? []), ...legacyItems] };
      });
      return;
    }

    const preorderItems = buildPreorderInvoiceItems({
      booking,
      invoiceTaxConfig,
      typeTaxAssignments,
      taxRates,
      taxLocking: invoiceTaxLocking,
      individualItemsEnabled,
      venuePackages,
      typesCache,
      venueConfig: venueConfig ?? undefined
    });

    if (preorderItems.length === 0) return;

    setInvoice((prev) => {
      if (!prev) return prev;
      const mergedItems = mergePreorderItemsIntoExisting(
        prev.items ?? [],
        preorderItems
      );
      return { ...prev, items: mergedItems };
    });
  }, [
    booking,
    invoiceTaxConfig,
    typeTaxAssignments,
    taxRates,
    invoiceTaxLocking,
    individualItemsEnabled,
    venuePackages,
    typesCache,
    venueConfig,
    venueMigrated
  ]);

  const handleAddInvoice = useCallback((): void => {
    // Pre-populate company details from booking (matches collins-ui addInvoice)
    const companyAddress = booking?.company_address;
    const newInvoice: InvoiceData = {
      date: new Date().toISOString(),
      items: [],
      custom_ref: "",
      client_ref: "",
      company: booking?.company ?? "",
      street: (companyAddress?.street as string | null) ?? "",
      city: (companyAddress?.city as string | null) ?? "",
      post_code: (companyAddress?.post_code as string | null) ?? ""
    };
    setInvoice(newInvoice);
    setIsEditing(true);
  }, [booking]);

  const handleEditInvoice = useCallback((): void => {
    setIsEditing(true);
    onClearSaveError?.();
  }, [onClearSaveError]);

  const handleCancelEdit = useCallback((): void => {
    if (invoiceIsDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    setIsEditing(false);
    onClearSaveError?.();
    if (latestInvoice) {
      setInvoice(latestInvoice);
    } else {
      setInvoice(null);
    }
  }, [latestInvoice, invoiceIsDirty, onClearSaveError]);

  const confirmCancelEdit = useCallback((): void => {
    setShowDiscardConfirm(false);
    setIsEditing(false);
    onClearSaveError?.();
    if (latestInvoice) {
      setInvoice(latestInvoice);
    } else {
      setInvoice(null);
    }
  }, [latestInvoice, onClearSaveError]);

  return {
    invoice,
    setInvoice,
    isEditing,
    setIsEditing,
    invoiceIsDirty,
    showDiscardConfirm,
    setShowDiscardConfirm,
    handleUpdateInvoice,
    handleUpdateItem,
    handleRemoveItem,
    handleAddBlankItem,
    handleAddRefundItems,
    handleAddDepositItems,
    handleAddPreorderItems,
    handleAddInvoice,
    handleEditInvoice,
    handleCancelEdit,
    confirmCancelEdit
  };
};

export default useInvoiceState;
